'use client';

import { useCallback } from 'react';
import { BaseLineUp2 } from '@/app/components/icons/BaseLineUp2';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { useSyncHeight } from '@/app/hooks';
import { useStoreApi } from '@/app/providers/store';
import { reportEvent } from '@/app/utils';
import { usePathname, useRouter } from '@/i18n/routing';

// 为了避免循环依赖，直接定义所需的接口
interface DetailItem {
  id: string;
  title: string;
  description: string;
  button: string;
  icon: React.ReactNode;
  backgroundImage: string;
  redirectUrl: string;
}

interface DetailCardProps {
  item: DetailItem;
  isMobileScreen?: boolean;
  renderBackgroundImage?: boolean;
}

/**
 * 详情卡片组件，展示项目详情和操作按钮
 */
export const DetailCard = ({ item, isMobileScreen = false, renderBackgroundImage = true }: DetailCardProps) => {
  const router = useRouter();
  const store = useStoreApi();
  const pathname = usePathname();

  useSyncHeight({ selector: '.detail-card' });

  // 抽象导航逻辑
  const handleNavigation = useCallback(() => {
    const userInfo = store.getState().userInfo;
    if (!userInfo) {
      reportEvent('login_page_exposure', {
        refer_element: `landing - ${item.redirectUrl.split('?')[0]}`,
        refer_page: pathname,
      });
    }
    router.push(item.redirectUrl);
  }, [router]);

  // 移动端和桌面端样式
  return isMobileScreen ? (
    <div className="relative flex h-[312px] w-full flex-col justify-between overflow-hidden rounded-[20px] bg-system-white12 p-4">
      <div className="relative z-10 flex flex-col items-start sm:w-[336px] md:w-[600px]">
        {item.icon}
        <div className="mt-4 w-[190px] font-semibold text-[18px] text-white">{item.title}</div>
        <div className="mt-3 w-[210px] text-[14px] text-white opacity-65">{item.description}</div>
      </div>
      <div
        className="z-10 flex w-fit items-center gap-2 rounded-full border-[2px] border-white border-solid px-6 py-3 text-[14px]"
        onClick={handleNavigation}
      >
        {item.button}
        <BaseLineUp2 className="size-5 rotate-90" />
      </div>
      {renderBackgroundImage ? (
        <img
          src={item.backgroundImage}
          alt={item.title}
          width={587}
          height={312}
          loading="lazy"
          decoding="async"
          className="absolute right-0 bottom-0 h-full w-full object-cover object-bottom"
        />
      ) : null}
    </div>
  ) : (
    <div className="detail-card relative flex min-h-[312px] w-[587px] flex-col justify-between overflow-hidden rounded-[20px] bg-system-white12 px-10 pt-6 pb-10">
      <div className="z-10 flex w-[288px] flex-col items-start lg:w-[200px]">
        {item.icon}
        <h3 className="mt-4 font-semibold text-[18px] text-white">{item.title}</h3>
        <div className="my-3 text-[14px] text-white opacity-65">{item.description}</div>
      </div>
      <ClientLink
        href={item.redirectUrl}
        className="z-10 flex w-fit cursor-pointer items-center gap-2 rounded-full border-[2px] border-white border-solid px-6 py-3 text-[14px]"
        onClick={(e) => {
          e.preventDefault();
          handleNavigation();
        }}
      >
        {item.button}
        <BaseLineUp2 className="size-5 rotate-90" />
      </ClientLink>
      {renderBackgroundImage ? (
        <img
          src={item.backgroundImage}
          alt={item.title}
          width={587}
          height={312}
          loading="lazy"
          decoding="async"
          className="absolute right-0 bottom-0 h-full w-full object-cover object-bottom"
        />
      ) : null}
    </div>
  );
};
